import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { BackendTypes, GetResponse } from '@tf/api';
import { BackendClient, gql, GraphQLClient } from '@tf/api';
import { formObjectUriBySegmentIdentityQueryOptions, type IdenfyResponse } from '@tf/shared';
import type { ObjectRelation } from '@tf/utils';
import { objectRelationValue, T } from '@tf/utils';

import type { CheckState } from './idenfyUtils';
import { getCheckRefetchInterval } from './idenfyUtils';

export type DocumentCheck = {
	id: string;
	meta: {
		'@createdAt': string;
		state: CheckState;
		country: string;
		dateOfBirth: string;
		documentKind: string;
		firstName: string;
		lastName: string;
		// external_id: string;
		service_kind: string;
	};
	data: {
		frontSide: ObjectRelation<'record_attachment'>;
		backSide?: ObjectRelation<'record_attachment'>;
		response?: IdenfyResponse;
	};
	attachments: {
		object: {
			id: string;
			meta: {
				'@createdAt': string;
				'@filename': string;
			};
		};
	}[];
};

const documentCheckQueryOptions = (docCheckObjectId?: string) => {
	return queryOptions({
		queryKey: ['documentCheck', docCheckObjectId],
		queryFn: async () => {
			T.assertNotNullish(docCheckObjectId, 'docCheckObjectId must not be null');
			const res = await GraphQLClient.getInstance().request<GetResponse<DocumentCheck>, { id: string }>(
				gql`
					query DocumentCheck($id: ID!) {
						get(rootFilter: { id: $id }) {
							id
							meta
							data
							attachments: nestedRelation(filter: { relationKind: "record_attachment" }) {
								object {
									id
									meta
								}
							}
						}
					}
				`,
				{ id: docCheckObjectId }
			);
			return res.get[0];
		},
		enabled: !!docCheckObjectId,
		refetchInterval: (query) => {
			return getCheckRefetchInterval(query.state.data);
		},
	});
};

export const useDocumentCheck = (docCheckObjectId?: string) => {
	return useQuery(documentCheckQueryOptions(docCheckObjectId));
};

export const useCreateDocumentCheckMutation = () => {
	return useMutation({
		mutationFn: async (checkParams: BackendTypes.DocumentCheckParams): Promise<string> => {
			const res = await BackendClient.getInstance().createDocumentCheck(checkParams);
			const check = await res.json();
			return check['@id'];
		},
	});
};

export const useSaveDocumentCheckIdMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			segmentIdentity,
			fieldPath,
			documentCheckId,
		}: {
			segmentIdentity: BackendTypes.SegmentIdentity;
			fieldPath: string;
			documentCheckId: string;
		}) => {
			const formObjectId = await queryClient.fetchQuery(
				formObjectUriBySegmentIdentityQueryOptions(segmentIdentity)
			);

			const jsonDataPath =
				fieldPath.replace(`${segmentIdentity.segmentKind}`, '').replaceAll('.', '/') +
				'/document_check_job';

			await GraphQLClient.getInstance().request(
				gql`
					mutation CreateDocumentCheck($formObjectId: ID!, $patch: JSONPatch!) {
						mutate(patch: [{ objectId: $formObjectId, objectDataPatch: [$patch] }]) {
							id
						}
					}
				`,
				{
					formObjectId,
					patch: {
						op: 'add',
						path: jsonDataPath,
						value: objectRelationValue('document_check', documentCheckId),
					},
				}
			);
		},
		onSuccess: () => {
			// invalidate segments to refetch it and get new revisions
			queryClient.invalidateQueries({ queryKey: ['segments'] });
		},
	});
};
